import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth, USER_TYPE_LABELS, NEEDS_VERIFICATION } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { LanguageSwitcher, TopicMultiSelect, ExpertiseEditor, expertisePayload } from "@/components/common";
import { COUNTRIES, isEUCountry } from "@/i18n/countries";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const { t } = useTranslation();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const inviteSlug = params.get("invite");
  const [areas, setAreas] = useState([]);
  const [f, setF] = useState({ name: "", email: "", password: "", user_type: "citizen", city: "", region: "", country: "Italy", eu: true });
  const [interests, setInterests] = useState([]);
  const [expertise, setExpertise] = useState([]);
  const [err, setErr] = useState(""), [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  useEffect(() => { api.get("/policy-areas").then((r) => setAreas(r.data.areas)); }, []);

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    const payload = {
      name: f.name, email: f.email, password: f.password, user_type: f.user_type,
      location: { city: f.city, region: f.region, country: f.country, eu: isEUCountry(f.country) },
      topics_interest: interests, expertise: expertisePayload(expertise),
      invite_slug: inviteSlug || undefined,
    };
    try {
      await register(payload);
      toast.success(NEEDS_VERIFICATION.includes(f.user_type) ? t("Registered — pending admin verification.") : t("Welcome! Your account is active."));
      nav("/dashboard");
    } catch (e2) { setErr(formatApiError(e2.response?.data?.detail) || e2.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-10">
      <div className="w-full max-w-xl">
        <div className="flex justify-end mb-4"><LanguageSwitcher /></div>
        <Link to="/" className="font-display font-extrabold text-2xl tracking-tight text-primary block text-center mb-8">Deliber<span className="text-secondary">.</span>Act</Link>
        <div className="border border-border bg-white p-8">
          <h1 className="font-display text-2xl font-bold tracking-tight mb-1">{t("Create your account")}</h1>
          <p className="text-sm text-muted-foreground mb-6">{t("Citizens are activated instantly. Organisations, officials and representatives are verified by an administrator.")}{inviteSlug && " " + t("You'll join the invited space automatically.")}</p>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label={t("Full name / Organisation")}><input data-testid="reg-name" required value={f.name} onChange={set("name")} className={ic} /></Field>
              <Field label={t("Email")}><input data-testid="reg-email" type="email" required value={f.email} onChange={set("email")} className={ic} /></Field>
            </div>
            <Field label={t("Password")}><input data-testid="reg-password" type="password" required minLength={6} value={f.password} onChange={set("password")} className={ic} /></Field>
            <Field label={t("Account type")}>
              <select data-testid="reg-user-type" value={f.user_type} onChange={set("user_type")} className={ic}>
                {Object.entries(USER_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{t(v)}</option>)}
              </select>
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field label={t("City")}><input data-testid="reg-city" value={f.city} onChange={set("city")} className={ic} /></Field>
              <Field label={t("State or Region")}><input value={f.region} onChange={set("region")} className={ic} /></Field>
              <Field label={t("Country")}>
                <select data-testid="reg-country" value={f.country} onChange={set("country")} className={ic}>
                  {COUNTRIES.map((c) => <option key={c.name} value={c.name}>{t(c.name)}</option>)}
                </select>
              </Field>
            </div>
            <p className="text-xs text-muted-foreground -mt-2">{t("The interface language is set automatically from your country. You can change it any time.")}</p>

            <Field label={t("Topics / Policy areas of interest")}>
              <TopicMultiSelect options={areas} value={interests} onChange={setInterests} testid="reg-topics" />
            </Field>

            <ExpertiseEditor options={areas} items={expertise} setItems={setExpertise} testid="reg-expertise" />

            {err && <p data-testid="reg-error" className="text-sm text-destructive">{err}</p>}
            <button data-testid="reg-submit" disabled={busy} className="w-full bg-primary text-white py-2.5 rounded-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-60">{busy ? t("Creating…") : t("Register")}</button>
          </form>
          <p className="text-sm text-muted-foreground mt-6 text-center">{t("Have an account?")} <Link to="/login" className="text-primary font-semibold">{t("Log in")}</Link></p>
        </div>
      </div>
    </div>
  );
}
const ic = "w-full border border-border bg-background rounded-sm px-3 py-2 focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none text-sm";
function Field({ label, children }) { return <div><label className="overline block mb-1">{label}</label>{children}</div>; }
